import { MenuItem } from '@mui/material';

import {
  FieldAttributesOptions,
  FieldTemplate,
  OptionSourceType,
} from 'api/models/FieldTemplate';
import ClearableSelect from 'components/ClearableSelect/ClearableSelect';
import { useTranslation } from 'react-i18next';
import { FieldTemplateFormProps } from '..';
import OptionAutocompleteField from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields/OptionAutocompleteField';
import { useParams } from 'react-router-dom';
import { GeneralIds, ModelTypes } from 'types/types';
import useRequest from 'hooks/useRequest';
import { query } from 'operations';

export default function SelectDefaultValueField({
  formik,
  disabled,
}: FieldTemplateFormProps) {
  const { t } = useTranslation();
  const {
    optionsSourceCodeLibraryId,
    isMultiSelect,
    defaultValue,
    options,
    optionsSourceFieldTemplateId,
    optionsSourceType,
  } = formik.values;

  const { projectId } = useParams<GeneralIds>();

  const { data: fieldOptions } = useRequest<FieldAttributesOptions[]>(
    async () => {
      if (!optionsSourceFieldTemplateId) {
        return options ?? [];
      }

      const { data: fields } = await query<FieldTemplate[]>({
        type: ModelTypes.fieldTemplate,
        ids: {
          projectId,
          id: optionsSourceFieldTemplateId,
        },
      });

      const field = fields[0];
      return field?.fieldAttributes.options ?? [];
    },
    {
      dependencies: [optionsSourceFieldTemplateId, options],
    },
  );

  switch (optionsSourceType) {
    case OptionSourceType.CodeLibrary:
      return (
        <OptionAutocompleteField
          onChange={(value: string | string[]) =>
            formik.setFieldValue('defaultValue', value)
          }
          value={defaultValue as string}
          defaultValue={defaultValue as string}
          isMultiSelect={isMultiSelect ?? false}
          optionsSourceCodeLibraryId={optionsSourceCodeLibraryId}
          label={t('forms.fieldAttributes.defaultValue')}
        />
      );
    case OptionSourceType.FieldTemplate:
    default:
      return (
        <ClearableSelect
          disabled={disabled}
          fullWidth
          multiple={isMultiSelect}
          isClearable={true}
          onClear={() =>
            formik.setFieldValue('defaultValue', isMultiSelect ? [] : null)
          }
          label={t('forms.fieldAttributes.defaultValue')}
          name="defaultValue"
          value={defaultValue}
          onChange={e => formik.setFieldValue('defaultValue', e.target.value)}
        >
          {fieldOptions?.map(
            (option: FieldAttributesOptions, index: number) => (
              <MenuItem
                key={`default-value-list-item-${option.value}-${option.label}`}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ),
          )}
        </ClearableSelect>
      );
  }
}
